import { cancelButtonProps } from "components/Form/components/CancelButton/CancelButton";

/**
 * The new-comment composer offers a Cancel button alongside Comment/Reply
 * (operator-task-fe-comment-action-buttons-affordance-and-cancel-on-new, Margot).
 * Cancelling clears the in-progress draft; because that discards typed text it is
 * guarded — when the draft holds unsaved text, Cancel interposes the app's shared
 * confirmation modal so an accidental click cannot silently lose a comment. This
 * is the same data-safety guard pattern the comment-delete control uses
 * (commentDelete.buildDeleteCommentConfirm).
 *
 * The decision ("does cancelling need a confirm?") and the modal CONFIG are pure
 * functions so the conventions can be unit-tested without rendering the whole
 * panel (mirroring commentDelete / commentEdit / commentRead / commentScroll).
 */

// Discarding is guarded only when there is actually unsaved text to lose; an
// empty (or whitespace-only) draft can be cancelled with no prompt.
export const shouldConfirmDiscardDraft = (draft: string): boolean =>
    draft.trim().length > 0;

export const DISCARD_COMMENT_CONFIRM_TITLE = "Discard comment";
export const DISCARD_COMMENT_CONFIRM_CONTENT =
    "Discard this comment? Your unsaved text will be lost.";

export const buildDiscardCommentConfirm = (onConfirm: () => void) => ({
    title: DISCARD_COMMENT_CONFIRM_TITLE,
    content: DISCARD_COMMENT_CONFIRM_CONTENT,
    onConfirm,
    confirmText: "Discard",
    // Disambiguate from the Cancel button that opened this dialog: the dialog's
    // own cancel means "do not discard".
    cancelText: "Keep editing",
    cancelButtonProps,
    // Discarding typed text is a data-loss action, so the confirm button uses the
    // shared danger affordance (ui-design-principle-buttons), matching the
    // comment-delete guard.
    confirmButtonProps: { className: "button button--danger" },
    className: "confirmationModal",
    centered: true,
});
